<template>
  <div ref="containerEl" class="pdf-page-canvas">
    <canvas
      v-show="hasRendered"
      ref="canvasEl"
      class="preview-image-shadow"
      :class="{
        'recently-change': recentlyChanged,
        'preview-image-shadow': !bigShadow,
        'preview-image-shadow-big': bigShadow,
      }"
    />
    <div v-if="rendering" class="pdf-page-canvas__loading">
      <ProgressSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import ProgressSpinner from "primevue/progressspinner";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

type PdfPageProxy = {
  getViewport: (params: { scale: number; rotation?: number }) => {
    width: number;
    height: number;
  };
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => {
    promise: Promise<void>;
    cancel?: () => void;
  };
  cleanup?: () => void;
};

type PdfDocumentProxy = {
  getPage: (pageNumber: number) => Promise<PdfPageProxy>;
};

const props = withDefaults(
  defineProps<{
    pdf: PdfDocumentProxy | null;
    pageNumber: number;
    scale?: number;
    fitMode?: "none" | "width" | "contain";
    fitWidth?: number;
    fitHeight?: number;
    maxScale?: number;
    minScale?: number;
    rotation?: number;
    padding?: number;
    active?: boolean;
    keepRendered?: boolean;
    recentlyChanged?: boolean;
    bigShadow?: boolean;
  }>(),
  {
    scale: 1.5,
    fitMode: "none",
    fitWidth: undefined,
    fitHeight: undefined,
    maxScale: 3,
    minScale: 0.01,
    rotation: undefined,
    padding: 0,
    active: true,
    keepRendered: true,
    recentlyChanged: false,
    bigShadow: false,
  },
);

const emit = defineEmits<{
  rendered: [
    payload: {
      pageNumber: number;
      width: number;
      height: number;
      scale: number;
    },
  ];
  error: [payload: { pageNumber: number; error: unknown }];
}>();

const containerEl = ref<HTMLDivElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);
const rendering = ref(false);
const hasRendered = ref(false);

let renderToken = 0;
let activeRenderTask: { cancel?: () => void } | null = null;
let resizeObserver: ResizeObserver | null = null;
let lastRenderKey = "";
let lastResizeKey = "";

function clampScale(scale: number) {
  return Math.min(props.maxScale, Math.max(props.minScale, scale));
}

function getContainerSizeKey() {
  const container = containerEl.value;

  if (!container || props.fitMode === "none") {
    return "";
  }

  const width = props.fitWidth ?? container.clientWidth;

  if (props.fitMode === "width") {
    return String(width);
  }

  return `${width}x${props.fitHeight ?? container.clientHeight}`;
}

async function calculateScale(page: PdfPageProxy) {
  if (props.fitMode === "none") {
    return clampScale(props.scale);
  }
  await nextTick();

  const container = containerEl.value;
  if (!container) {
    return clampScale(props.scale);
  }

  const baseViewport = page.getViewport({
    scale: 1,
    ...(props.rotation == null ? {} : { rotation: props.rotation }),
  });
  const userScale = clampScale(props.scale);

  const containerRect = container.getBoundingClientRect();
  const fitWidth = props.fitWidth ?? (container.clientWidth || containerRect.width);
  const fitHeight =
    props.fitHeight ??
    (container.clientHeight || containerRect.height || container.parentElement?.clientHeight || 1);
  const availableWidth = Math.max(1, fitWidth - props.padding * 2);
  const availableHeight = Math.max(1, fitHeight - props.padding * 2);

  if (props.fitMode === "width") {
    return (availableWidth / baseViewport.width) * userScale;
  }

  const widthScale = availableWidth / baseViewport.width;
  const heightScale = availableHeight / baseViewport.height;

  return Math.min(widthScale, heightScale) * userScale;
}

function clearCanvas() {
  const canvas = canvasEl.value;
  const ctx = canvas?.getContext("2d");

  if (canvas && ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  hasRendered.value = false;
  lastRenderKey = "";
}

async function renderPage(force = false) {
  const pdf = props.pdf;
  const canvas = canvasEl.value;

  if (!pdf || !canvas || props.pageNumber < 1 || !props.active) {
    return;
  }

  const renderKey = [
    props.pageNumber,
    props.scale,
    props.fitMode,
    props.rotation,
    getContainerSizeKey(),
  ].join("|");

  if (!force && props.keepRendered && hasRendered.value && renderKey === lastRenderKey) {
    return;
  }

  const token = ++renderToken;
  rendering.value = true;

  try {
    activeRenderTask?.cancel?.();

    const page = await pdf.getPage(props.pageNumber);
    if (token !== renderToken) {
      return;
    }

    const finalScale = await calculateScale(page);
    if (token !== renderToken) {
      return;
    }

    const viewport = page.getViewport({
      scale: finalScale,
      ...(props.rotation == null ? {} : { rotation: props.rotation }),
    });

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.ceil(viewport.width * dpr);
    canvas.height = Math.ceil(viewport.height * dpr);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);

    const renderTask = page.render({
      canvasContext: ctx,
      viewport,
    });
    activeRenderTask = renderTask;

    await renderTask.promise;
    if (token !== renderToken) {
      return;
    }

    page.cleanup?.();

    hasRendered.value = true;
    lastRenderKey = renderKey;

    emit("rendered", {
      pageNumber: props.pageNumber,
      width: viewport.width,
      height: viewport.height,
      scale: finalScale,
    });
  } catch (error: unknown) {
    if ((error as { name?: string })?.name !== "RenderingCancelledException") {
      emit("error", { pageNumber: props.pageNumber, error });
    }
  } finally {
    if (token === renderToken) {
      rendering.value = false;
      activeRenderTask = null;
    }
  }
}

watch(
  () =>
    [
      props.pdf,
      props.pageNumber,
      props.scale,
      props.fitMode,
      props.rotation,
      props.fitWidth,
      props.fitHeight,
      props.active,
    ] as const,
  async ([pdf, , , , , , , active]) => {
    if (!pdf) {
      clearCanvas();
      return;
    }

    if (!active) {
      return;
    }

    await nextTick();
    await renderPage();
  },
  { immediate: true, flush: "post" },
);

onMounted(async () => {
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => {
      const resizeKey = getContainerSizeKey();

      if (props.fitMode !== "none" && resizeKey !== lastResizeKey) {
        lastResizeKey = resizeKey;
        void renderPage(true);
      }
    });

    if (containerEl.value) {
      lastResizeKey = getContainerSizeKey();
      resizeObserver.observe(containerEl.value);
    }
  }

  await nextTick();
  await renderPage();
});

onBeforeUnmount(() => {
  renderToken++;
  activeRenderTask?.cancel?.();
  resizeObserver?.disconnect();
});
</script>

<style scoped lang="scss">
@use "@/modules/filemanagement/components/FMFileViewer/FMFileViewer.scss" as *;
.pdf-page-canvas {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
}

.pdf-page-canvas canvas {
  display: block;
  background: var(--p-white);
}

.pdf-page-canvas__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
